import { BaseWebComponentView } from '../../../../../../framework/BaseWebComponentView';
import {
  ANTIMALWARE_EXCLUSION_TYPE,
  Policy,
  PolicyCounters,
  SecurityTelemetrySiemType,
} from '@nirvana/shared/dtos/types/policies/common';
import { PolicyPanelManager } from '../../../PolicyPanelManager';
import { TemplateState } from '../../../../../../types/common/types';
import { PolicyContext } from '../../../redux/navigationHeaderSlice/types.navigation';
import { GzSearchablePolicyPanelViewWebComponent } from '../../searchablePolicyPanelView';
import { translateFn } from '../../../../../../utils/translator.helper';
import { store } from '@nirvana/redux/store';
import { DataLoadedStatus } from '../../../redux/types';
import {
  setValidationLoadedForGeneralDetails,
  setValidationLoadedForGeneralSecurityTelemetry,
} from '../../../redux/customStates/general/general.actions';

export class GzSearchablePolicyPanelView extends BaseWebComponentView {
  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$errorMessage = this.shadow.querySelector('#error-message');
    this.dom.$sesarchableAccordionsView = this.shadow.querySelector(
      'gz-searchable-policy-panel-view'
    ) as GzSearchablePolicyPanelViewWebComponent;

    this.setLoadingState();
  }

  buildSuccessView(data: {
    routingPath: string;
    policy: Policy;
    phantom: boolean;
    context: PolicyContext;
    counters?: PolicyCounters;
  }) {
    this.dom.$sesarchableAccordionsView.setDataView(data);

    this.setAdditionalPolicyInfo(data.policy, data.phantom, data.counters);
    this.removeLoadingState();
  }

  private setAdditionalPolicyInfo(policy: Policy, phantom: boolean, counters?: PolicyCounters) {
    if (!policy.default) {
      this.setPolicyNameValidity(policy, phantom);
    } else {
      store.dispatch(setValidationLoadedForGeneralDetails(DataLoadedStatus.SUCCESS));
    }

    if (PolicyPanelManager.getInstance().isGeneralSecurityTelemetryAvailable()) {
      this.setServerUrlValidity(policy, phantom);
    }
    if (PolicyPanelManager.getInstance().isPatchManagementModuleAvailable()) {
      this.setPatchManagementAvailableMaintenanceWindowsPolicyInfo(policy, phantom);
    }

    if (PolicyPanelManager.getInstance().isContentControlWebSectionAvailable()) {
      this.setWebAccessControlSchedulesPolicyInfo(policy, phantom);
    }

    if (PolicyPanelManager.getInstance().isIntegrityMonitorModuleAvailable()) {
      this.setCompanyRuleSetsForPolicy(policy, phantom);
      this.setRuleSetsForPolicy(policy);
    }

    this.setAntimalwareExclusionsListsForPolicy(policy, phantom);
    this.setAntimalwareVendorsExclusionsListsForPolicy();
    PolicyPanelManager.getInstance().setCounters(counters);
  }

  private async setPolicyNameValidity(policy: Policy, phantom: boolean) {
    try {
      // policyId should be taken into account only on add
      const response = await this.dependencies.api.clients.PolicyTemplates.isPolicyValidObservable(
        policy.name,
        phantom ? null : policy._id
      ).toPromise();
      PolicyPanelManager.getInstance().setGeneralDetailsValidation(policy.name, response.success);
    } catch (e) {
      PolicyPanelManager.getInstance().setGeneralDetailsValidation(policy.name);
    }
  }

  private async setServerUrlValidity(policy: Policy, phantom: boolean) {
    if (
      policy.uiSettings.general.securityTelemetry.siem !== SecurityTelemetrySiemType.SPLUNK &&
      !policy.uiSettings.general.securityTelemetry.enabled
    ) {
      store.dispatch(setValidationLoadedForGeneralSecurityTelemetry(DataLoadedStatus.SUCCESS));
      return;
    }
    const serverUrlValue = policy.uiSettings.general.securityTelemetry.config.url;
    try {
      const response = await this.dependencies.api.clients.PolicyTemplates.verifySecurityTelemetryUrlObservable(
        serverUrlValue
      ).toPromise();
      PolicyPanelManager.getInstance().setGeneralSecurityTelemetryValidation(serverUrlValue, response.success);
    } catch (e) {
      PolicyPanelManager.getInstance().setGeneralSecurityTelemetryValidation(serverUrlValue);
    }
  }

  private async setPatchManagementAvailableMaintenanceWindowsPolicyInfo(policy: Policy, phantom: boolean) {
    const selectedMaintenanceWindowId =
      policy.uiSettings.patchManagement.maintenanceWindows.length > 0
        ? policy.uiSettings.patchManagement.maintenanceWindows[0].id
        : '';
    const companyId = phantom ? Application.Main.user.company.id : policy.company?.id;

    try {
      const response = await this.dependencies.api.clients.MaintenanceWindows.GetMaintenanceWindowsForPolicy(
        0,
        companyId,
        selectedMaintenanceWindowId
      ).toPromise();
      PolicyPanelManager.getInstance().setPatchManagementAvailableMaintenanceWindowsPolicyInfo(response);
    } catch (error) {
      PolicyPanelManager.getInstance().setPatchManagementAvailableMaintenanceWindowsErrorState();
    }
  }

  private async setWebAccessControlSchedulesPolicyInfo(policy: Policy, phantom: boolean) {
    const selectedScheduleId = policy.uiSettings.contentControl.webCategoriesFilter.scheduleId;
    const companyId = phantom ? Application.Main.user.company.id : policy.company?.id;

    try {
      const response = await this.dependencies.api.clients.NetworkProtection.GetCompanySchedules(
        companyId,
        selectedScheduleId
      ).toPromise();
      PolicyPanelManager.getInstance().setContentControlWebAvailableSchedulersPolicyInfo(response);
    } catch (error) {
      PolicyPanelManager.getInstance().setContentControlWebAvailableSchedulersPolicyInfoErrorState();
    }
  }

  private async setCompanyRuleSetsForPolicy(policy: Policy, phantom: boolean) {
    const companyId = phantom ? Application.Main.user.company.id : policy.company?.id;

    try {
      const response = await this.dependencies.api.clients.IntegrityMonitorRuleSets.GetCompanyRuleSetsForPolicy(
        companyId
      ).toPromise();
      PolicyPanelManager.getInstance().setIntegrityMonitorCompanyRuleSetsPolicyInfo(response.data);
    } catch (error) {
      PolicyPanelManager.getInstance().setIntegrityMonitorCompanyRuleSetsErrorState();
    }
  }

  private async setAntimalwareExclusionsListsForPolicy(policy: Policy, phantom: boolean) {
    const companyId = phantom ? Application.Main.user.company.id : policy.company?.id;
    const selectedListsIds = policy.uiSettings.antimalware.settings.activateExclusions.exclusionsLists ?? [];

    try {
      const response = await this.dependencies.api.clients.Antimalware.GetCompanyExclusionLists(
        companyId,
        ANTIMALWARE_EXCLUSION_TYPE,
        { includeDescription: true, includeEndpointsCounts: true, selectedListsIds: selectedListsIds }
      ).toPromise();
      PolicyPanelManager.getInstance().setAntimalwareExclusionListsPolicyInfo(response.data);
    } catch (error) {
      PolicyPanelManager.getInstance().setAntimalwareExclusionListsErrorState();
    }
  }

  private async setAntimalwareVendorsExclusionsListsForPolicy() {
    try {
      const response = await this.dependencies.api.clients.Antimalware.GetVendorsLists().toPromise();
      PolicyPanelManager.getInstance().setAntimalwareVendorsExclusionListsPolicyInfo(response);
    } catch (error) {
      PolicyPanelManager.getInstance().setAntimalwareVendorsExclusionListsErrorState();
    }
  }

  private async setRuleSetsForPolicy(policy: Policy) {
    try {
      const response = await this.dependencies.api.clients.IntegrityMonitorRuleSets.GetRuleSetsForPolicy(
        policy.uiSettings.integrityMonitor.realTime.ruleSetIds
      ).toPromise();
      PolicyPanelManager.getInstance().setIntegrityMonitorRuleSetsPolicyInfo(response.data);
    } catch (error) {
      console.error('Error getting rule sets', error);
      PolicyPanelManager.getInstance().setIntegrityMonitorRuleSetsErrorState();
    }
  }

  buildErrorView() {
    this.dom.$errorMessage.innerText = translateFn('Policy not found or inaccessible');
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }
}
